// AddPayment.jsx

import React, { useState, useMemo } from "react";
import { 
  Calendar, DollarSign, ArrowLeft, FileText, CreditCard, 
  Banknote, Receipt, TrendingUp, Save, Eye, Clock, AlertCircle
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { payQuota } from "../../../../services/quotasService";
import Swal from "sweetalert2";
import { ActionLoader } from "../../../loader";

const ManualPaymentEntry = ({ 
  selectedQuotas = [], 
  calculatedTotal = 0,
  onBack, 
  onSavePayment,
  socio = { business_name: "Empresa Demo S.A." }, 
  show = true 
}) => {
  const [formData, setFormData] = useState({
    amount: calculatedTotal,
    payment_method: 'Transferencia',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
  };

  // Validaciones
  const validateForm = () => {
    const newErrors = {};
    if (!formData.amount || formData.amount <= 0) newErrors.amount = 'El monto debe ser mayor a 0';
    if (!formData.payment_method) newErrors.payment_method = 'Seleccione un método de pago';
    if (!formData.payment_date) newErrors.payment_date = 'La fecha de pago es requerida';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Obtener ícono del método de pago
  const getPaymentMethodIcon = (method) => {
    const icons = {
      'transferencia': <CreditCard size={16} className="text-blue-600" />,
      'efectivo': <Banknote size={16} className="text-green-600" />,
      'cheque': <Receipt size={16} className="text-purple-600" />,
      'default': <DollarSign size={16} className="text-gray-600" />
    };
    return icons[method?.toLowerCase()] || icons.default;
  };

  // Estadísticas de las cuotas
  const quotaStats = useMemo(() => ({
    totalQuotas: selectedQuotas.length,
    uniquePeriods: new Set(selectedQuotas.map(q => `${q.period} ${q.year}`)).size,
    totalAmount: selectedQuotas.reduce((sum, quota) => sum + parseFloat(quota.amount), 0)
  }), [selectedQuotas]);

  // Manejar envío del formulario
  const handleSubmit = async () => {
    if (!validateForm()) return;

    const confirmation = await Swal.fire({
      title: 'Confirmar Pago',
      text: `¿Está seguro de registrar el pago de ${formatCurrency(calculatedTotal)} para las cuotas seleccionadas?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, registrar pago',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'bg-[#1BBB55] text-white hover:bg-[#1aab51]',
        cancelButton: 'bg-[#DC3465] text-white hover:bg-[#c02e59]',
      }
    });

    if (!confirmation.isConfirmed) return;
    const paymentData = {
      ...formData,
      social_quotas: selectedQuotas.map(quota => ({ social_quota_id: quota.id })),
      status: 'completado',
      companyRut: socio.rut
    };
    
    try {
      setIsSubmitting(true);
      const response = await payQuota(paymentData);
      
      if (response?.success) {
        await Swal.fire({
          title: 'Pago Registrado',
          text: `El pago de ${formatCurrency(calculatedTotal)} ha sido registrado exitosamente.`,
          icon: 'success',
          confirmButtonText: 'Aceptar',
          customClass: { confirmButton: 'bg-[#1BBB55] text-white hover:bg-[#1aab51]' }
        });
        onSavePayment(response);
        setFormData({ amount: calculatedTotal, payment_method: 'Transferencia', payment_date: new Date().toISOString().split('T')[0], notes: '' });
      } else {
        throw new Error(response?.message || 'Error al registrar el pago');
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error al guardar pago',
        text: error.message || 'Ocurrió un error al intentar registrar el pago.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        customClass: { confirmButton: 'bg-[#DC3465] text-white hover:bg-[#c02e59]' }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.amount > 0 && formData.payment_method && formData.payment_date && selectedQuotas.length > 0;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#09274A] to-[#0488AF] p-6 text-white">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <DollarSign size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Registrar Pago Manual</h2>
                  <p className="text-white/90 mt-1">{socio?.business_name}</p>
                </div>
              </div>
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2 bg-[#DC3465] hover:bg-[#c02e59] rounded-lg transition-colors"
              >
                <ArrowLeft size={16} />
                Volver
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {[
                { icon: Receipt, value: quotaStats.totalQuotas, label: "Cuotas a Pagar", color: "blue" },
                { icon: Calendar, value: quotaStats.uniquePeriods, label: "Períodos", color: "green" },
                { icon: TrendingUp, value: formatCurrency(quotaStats.totalAmount), label: "Total Calculado", color: "purple" }
              ].map(({ icon: Icon, value, label, color }, idx) => (
                <div key={idx} className={`bg-gradient-to-br from-${color}-50 to-${color}-100 p-4 rounded-xl border border-${color}-200`}>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 bg-${color}-500 rounded-lg`}>
                      <Icon className="text-white" size={16} />
                    </div>
                    <div>
                      <div className={`text-2xl font-bold text-${color}-700`}>{value}</div>
                      <div className={`text-sm text-${color}-600`}>{label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Cuotas Seleccionadas */}
            {selectedQuotas.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Eye className="text-blue-600" size={18} />
                  Cuotas Seleccionadas
                </h3>
                <div className="bg-gradient-to-r from-gray-50 to-gray-50/50 border border-gray-200 rounded-xl p-4">
                  <div className="grid gap-2 max-h-48 overflow-y-auto">
                    {selectedQuotas.map((quota, index) => (
                      <motion.div
                        key={quota.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-semibold text-xs">{index + 1}</span>
                          </div>
                          <span className="font-medium text-gray-800 text-sm">{quota.period} {quota.year}</span>
                          <div className="flex items-center gap-1 text-xs text-yellow-600">
                            <Clock size={8} />
                            <span className="capitalize">{quota.status}</span>
                          </div>
                        </div>
                        <div className="font-bold text-green-600 text-sm">
                          {formatCurrency(parseFloat(quota.amount))}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Formulario */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-50/50 border border-gray-200 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="text-blue-600" size={18} />
                Información del Pago
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Monto */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Monto del Pago *</label>
                    <div className="relative">
                      <DollarSign size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="number"
                        value={formData.amount}
                        onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                          errors.amount ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    {errors.amount && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle size={12} />
                        {errors.amount}
                      </p>
                    )}
                  </div>

                  {/* Método de pago */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Método de Pago *</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                        {getPaymentMethodIcon(formData.payment_method)}
                      </div>
                      <select
                        value={formData.payment_method}
                        onChange={(e) => handleInputChange('payment_method', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none bg-white"
                      >
                        <option value="Transferencia">Transferencia</option>
                        <option value="Efectivo">Efectivo</option>
                        <option value="Depósito">Depósito</option>
                        <option value="Cheque">Cheque</option>
                      </select>
                    </div>
                  </div>

                  {/* Fecha */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Pago *</label>
                    <div className="relative">
                      <Calendar size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="date"
                        value={formData.payment_date}
                        onChange={(e) => handleInputChange('payment_date', e.target.value)}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                          errors.payment_date ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                      />
                    </div>
                    {errors.payment_date && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle size={12} />
                        {errors.payment_date}
                      </p>
                    )}
                  </div>
                </div>

                {/* Notas */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notas Adicionales</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                    rows="2"
                    placeholder="Información adicional sobre el pago (opcional)"
                  />
                </div>

                {/* Botones */}
                <div className="flex gap-3 pt-3 border-t border-gray-200">
                  <button
                    onClick={onBack}
                    className="flex-1 px-4 py-3 border border-gray-300 text-white rounded-lg bg-[#DC3465] hover:bg-[#c02e59] transition-colors"
                  >
                    Cancelar
                  </button>
                  
                  <button
                    onClick={handleSubmit}
                    disabled={!isFormValid || isSubmitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#1BBB55] text-white rounded-lg hover:bg-[#1aab51] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Registrar Pago
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <ActionLoader open={isSubmitting} onClose={() => {}} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ManualPaymentEntry;